import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { money } from '../lib/money';
import { toast } from 'sonner';

const inputCls = 'border border-slate-200 rounded-md px-2 py-2 text-xs bg-white w-full';
const badge = (s) => ({ awarded: 'bg-amber-100 text-amber-700', disbursed: 'bg-emerald-100 text-emerald-700', revoked: 'bg-slate-100 text-slate-400' }[s] || 'bg-slate-100 text-slate-600');

export const FinancialAidPanel = () => {
  const qc = useQueryClient();
  const { data: summary } = useQuery({ queryKey: ['finaid-summary'], queryFn: () => api.get('/finaid/summary').then((r) => r.data) });
  const { data: schemes } = useQuery({ queryKey: ['finaid-schemes'], queryFn: () => api.get('/finaid/schemes').then((r) => r.data) });
  const { data: awards } = useQuery({ queryKey: ['finaid-awards'], queryFn: () => api.get('/finaid/awards').then((r) => r.data) });
  const { data: students } = useQuery({ queryKey: ['students'], queryFn: () => api.get('/students').then((r) => r.data) });
  const { data: invoices } = useQuery({ queryKey: ['invoices'], queryFn: () => api.get('/invoices').then((r) => r.data) });
  const inval = () => ['finaid-summary', 'finaid-schemes', 'finaid-awards', 'invoices', 'arrears', 'fin-analytics'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const [sf, setSf] = useState({ name: '', aid_type: 'amount', value: '', budget_cap: '', academic_year: '2026-27', criteria: '' });
  const schemeMut = useMutation({
    mutationFn: () => api.post('/finaid/schemes', { ...sf, value: parseFloat(sf.value), budget_cap: sf.budget_cap ? parseFloat(sf.budget_cap) : null }),
    onSuccess: () => { toast.success('Scholarship scheme created'); setSf({ ...sf, name: '', value: '', budget_cap: '', criteria: '' }); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, active }) => api.patch(`/finaid/schemes/${id}`, { active }),
    onSuccess: () => inval(), onError: (e) => toast.error(errMsg(e)),
  });

  const [af, setAf] = useState({ scheme_id: '', student_id: '', notes: '' });
  const awardMut = useMutation({
    mutationFn: () => api.post('/finaid/awards', af),
    onSuccess: () => { toast.success('Scholarship awarded'); setAf({ scheme_id: '', student_id: '', notes: '' }); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const [disbursePick, setDisbursePick] = useState({});
  const disburseMut = useMutation({
    mutationFn: ({ id, invoice_id }) => api.post(`/finaid/awards/${id}/disburse`, { invoice_id }),
    onSuccess: (r) => { toast.success(`${money(r.data.amount)} waived on the invoice - balance now ${money(r.data.invoice_balance)}`); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const revokeMut = useMutation({
    mutationFn: (id) => api.delete(`/finaid/awards/${id}`),
    onSuccess: () => { toast.success('Award revoked'); inval(); }, onError: (e) => toast.error(errMsg(e)),
  });

  const activeStudents = (students || []).filter((s) => s.status === 'active');
  const openInvoicesFor = (sid) => (invoices || []).filter((i) => i.student_id === sid && i.balance > 0 && i.status !== 'written_off');
  const cards = [
    { label: 'Active schemes', value: summary?.active_schemes ?? 0, test: 'aid-card-schemes' },
    { label: 'Awards', value: summary?.awards ?? 0, test: 'aid-card-awards' },
    { label: 'Awaiting disbursement', value: summary?.pending_awards ?? 0, test: 'aid-card-pending' },
    { label: 'Aid disbursed', value: money(summary?.disbursed_total), test: 'aid-card-disbursed' },
  ];

  return (
    <div data-testid="finaid-panel">
      <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-4">Aid posts into the existing invoice ledger as waivers - statements, dunning and analytics stay consistent</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-slate-200 rounded-md p-4" data-testid={c.test}>
            <div className="font-mono-label text-[10px] uppercase text-slate-400">{c.label}</div>
            <div className="text-xl font-bold mt-1">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-5" data-testid="aid-scheme-form">
        <div className="font-mono-label text-[10px] uppercase text-slate-400 mb-3">New scholarship scheme</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-2">
          <input data-testid="aid-scheme-name-input" placeholder="Name (e.g. Merit Scholarship)" value={sf.name} onChange={(e) => setSf({ ...sf, name: e.target.value })} className={`${inputCls} lg:col-span-2`} />
          <select data-testid="aid-scheme-type-select" value={sf.aid_type} onChange={(e) => setSf({ ...sf, aid_type: e.target.value })} className={inputCls}>
            <option value="amount">Fixed amount</option>
            <option value="percent">% of invoice</option>
          </select>
          <input data-testid="aid-scheme-value-input" type="number" placeholder={sf.aid_type === 'percent' ? '% value' : 'Amount'} value={sf.value} onChange={(e) => setSf({ ...sf, value: e.target.value })} className={inputCls} />
          <input data-testid="aid-scheme-cap-input" type="number" placeholder="Budget cap (optional)" value={sf.budget_cap} onChange={(e) => setSf({ ...sf, budget_cap: e.target.value })} className={inputCls} />
          <button data-testid="aid-create-scheme-button" disabled={!sf.name || !sf.value || schemeMut.isPending} onClick={() => schemeMut.mutate()}
            className="text-xs bg-slate-900 text-white rounded px-3 py-2 hover:bg-slate-800 disabled:opacity-40">Create scheme</button>
        </div>
        <input data-testid="aid-scheme-criteria-input" placeholder="Eligibility criteria note (optional)" value={sf.criteria} onChange={(e) => setSf({ ...sf, criteria: e.target.value })} className={`${inputCls} mt-2`} />
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto mb-6" data-testid="aid-schemes-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left">
            {['Scheme', 'Aid', 'Budget cap', 'Disbursed', 'Awards', 'Status', ''].map((h, i) => <th key={i} className="py-2 px-3 font-mono-label text-[10px] uppercase text-slate-400">{h}</th>)}
          </tr></thead>
          <tbody>
            {(schemes || []).map((s, i) => (
              <tr key={s.id} className={`border-t border-slate-100 ${i % 2 ? 'bg-slate-50/50' : ''}`} data-testid={`aid-scheme-row-${s.id}`}>
                <td className="py-2 px-3"><span className="font-medium">{s.name}</span>{s.criteria && <div className="text-[10px] text-slate-400">{s.criteria}</div>}</td>
                <td className="py-2 px-3">{s.aid_type === 'percent' ? `${s.value}%` : money(s.value)}</td>
                <td className="py-2 px-3">{s.budget_cap ? money(s.budget_cap) : '—'}</td>
                <td className="py-2 px-3">{money(s.disbursed)}</td>
                <td className="py-2 px-3">{s.awards}</td>
                <td className="py-2 px-3"><span className={`text-[10px] px-2 py-0.5 rounded-full ${s.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>{s.active ? 'active' : 'inactive'}</span></td>
                <td className="py-2 px-3"><button data-testid={`aid-toggle-scheme-${s.id}`} onClick={() => toggleMut.mutate({ id: s.id, active: !s.active })} className="text-xs underline text-slate-400">{s.active ? 'deactivate' : 'activate'}</button></td>
              </tr>
            ))}
            {(schemes || []).length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">No scholarship schemes yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-5" data-testid="aid-award-form">
        <div className="font-mono-label text-[10px] uppercase text-slate-400 mb-3">Award a scholarship</div>
        <div className="grid sm:grid-cols-3 gap-2">
          <select data-testid="aid-award-scheme-select" value={af.scheme_id} onChange={(e) => setAf({ ...af, scheme_id: e.target.value })} className={inputCls}>
            <option value="">Pick a scheme…</option>
            {(schemes || []).filter((s) => s.active).map((s) => <option key={s.id} value={s.id}>{s.name} ({s.aid_type === 'percent' ? `${s.value}%` : money(s.value)})</option>)}
          </select>
          <select data-testid="aid-award-student-select" value={af.student_id} onChange={(e) => setAf({ ...af, student_id: e.target.value })} className={inputCls}>
            <option value="">Pick a student…</option>
            {activeStudents.map((s) => <option key={s.id} value={s.id}>{s.name} (Grade {s.grade})</option>)}
          </select>
          <button data-testid="aid-award-button" disabled={!af.scheme_id || !af.student_id || awardMut.isPending} onClick={() => awardMut.mutate()}
            className="text-xs bg-slate-900 text-white rounded px-3 py-2 hover:bg-slate-800 disabled:opacity-40">Award scholarship</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto" data-testid="aid-awards-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left">
            {['Student', 'Scheme', 'Aid', 'Status', 'Disbursed to', 'Actions'].map((h) => <th key={h} className="py-2 px-3 font-mono-label text-[10px] uppercase text-slate-400">{h}</th>)}
          </tr></thead>
          <tbody>
            {(awards || []).map((a, i) => {
              const openInvs = a.status === 'awarded' ? openInvoicesFor(a.student_id) : [];
              return (
                <tr key={a.id} className={`border-t border-slate-100 ${i % 2 ? 'bg-slate-50/50' : ''}`} data-testid={`aid-award-row-${a.id}`}>
                  <td className="py-2 px-3 font-medium">{a.student_name} <span className="text-xs text-slate-400 font-normal">Gr {a.grade}</span></td>
                  <td className="py-2 px-3">{a.scheme_name}</td>
                  <td className="py-2 px-3">{a.status === 'disbursed' ? money(a.disbursed_amount) : (a.aid_type === 'percent' ? `${a.value}%` : money(a.value))}</td>
                  <td className="py-2 px-3"><span className={`text-[10px] px-2 py-0.5 rounded-full ${badge(a.status)}`}>{a.status}</span></td>
                  <td className="py-2 px-3 text-xs">{a.status === 'disbursed' ? (a.plan_name || a.invoice_id?.slice(0, 8)) : '—'}</td>
                  <td className="py-2 px-3">
                    {a.status === 'awarded' && (
                      <div className="flex gap-1 items-center flex-wrap">
                        <select data-testid={`aid-disburse-invoice-${a.id}`} value={disbursePick[a.id] || ''} onChange={(e) => setDisbursePick({ ...disbursePick, [a.id]: e.target.value })}
                          className="border border-slate-200 rounded px-1.5 py-1 text-[11px] bg-white max-w-[200px]">
                          <option value="">Pick invoice…</option>
                          {openInvs.map((inv) => <option key={inv.id} value={inv.id}>{inv.plan_name} — bal {money(inv.balance, inv.currency)}</option>)}
                        </select>
                        <button data-testid={`aid-disburse-button-${a.id}`} disabled={!disbursePick[a.id] || disburseMut.isPending}
                          onClick={() => disburseMut.mutate({ id: a.id, invoice_id: disbursePick[a.id] })}
                          className="text-[11px] bg-emerald-600 text-white rounded px-2 py-1 disabled:opacity-40">Disburse</button>
                        <button data-testid={`aid-revoke-button-${a.id}`} onClick={() => window.confirm(`Revoke ${a.scheme_name} for ${a.student_name}?`) && revokeMut.mutate(a.id)}
                          className="text-[11px] underline text-slate-400">revoke</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {(awards || []).length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-400">No scholarships awarded yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
